import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Header from "../components/landing/Header";
import Footer from "../components/landing/Footer";
import { scrollTo } from "../components/landing/Header";
import { FEATURE_GROUPS, FEATURE_PAGES, featureHref } from "../lib/featurePages";

export default function FeaturesIndexPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Product features — simplyOwner";
  }, []);

  return (
    <main data-testid="features-index" className="relative">
      <Header />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-16">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-mono text-xs tracking-[0.3em] uppercase text-brand mb-6"
        >
          Product
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-display font-black uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-ink max-w-4xl"
        >
          Everything you need to own your customers.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-6 text-ink/65 text-lg font-light max-w-2xl leading-relaxed"
        >
          Website, shop, partner app, native customer apps and marketing tools — one one-time package, 0% commission. Open any page below.
        </motion.p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 space-y-16">
        {FEATURE_GROUPS.map((group) => (
          <div key={group.title}>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-black/40 mb-5">{group.title}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group.slugs.map((slug) => {
                const item = FEATURE_PAGES[slug];
                if (!item) return null;
                return (
                  <Link
                    key={slug}
                    to={featureHref(slug)}
                    data-testid={`features-index-${slug}`}
                    className="group rounded-xl border border-black/10 bg-white px-6 py-6 hover:border-brand/60 transition-colors duration-300"
                  >
                    <div className="font-display font-bold text-lg text-ink group-hover:text-brand transition-colors duration-200">
                      {item.title}
                    </div>
                    <p className="mt-2 text-sm text-ink/55 font-light leading-relaxed">{item.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-brand">
                      Open page <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        <div className="rounded-2xl bg-black text-white p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl">See it with your menu.</h2>
            <p className="mt-3 text-white/60 font-light max-w-xl">Book a live demo or become the owner. Live in under 7 days.</p>
          </div>
          <button
            data-testid="features-index-cta"
            onClick={() => scrollTo("/#contact")}
            className="group shrink-0 flex items-center gap-3 bg-brand hover:bg-white hover:text-black text-white font-display font-bold uppercase tracking-wide px-9 py-4 rounded-full transition-colors duration-300"
          >
            Become the owner
            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </div>
      </section>
      <Footer />
    </main>
  );
}
